<template>
    <Spinner v-if="showSpinner" />
    <router-view />

    <!-- Overlay components-->
    <Dialog
        id="selectorg"
        header="Select your organization"
        v-model:visible="showOrganizationSelector"
        :closable="false"
        :modal="true"
    >
        <Listbox
            :options="organizations"
            optionLabel="name"
            style="width: 15rem"
            @change="selectOrganization"
        />
    </Dialog>
    <Dialog
        id="selectrole"
        header="Select your role"
        v-model:visible="showRoleSelector"
        :closable="false"
        :modal="true"
    >
    </Dialog>
    <Dialog
        id="selectstek"
        header="Select the Stek"
        v-model:visible="showStekSelector"
        :closable="false"
        :modal="true"
    >
        <Listbox
            :options="steks"
            optionLabel="name"
            style="width: 15rem"
            @change="selectStek"
        />
    </Dialog>
</template>

<script lang="ts" setup>
import Spinner from "@/shared/Spinner.vue";
import { computed, ref } from "vue";
import { useRoute, useRouter } from "vue-router";

import { useOrganizationManager } from "@/composables/organization-manager";
import { useStekManager } from "@/composables/stek-manager";
import { useOrganizationsManager } from "@/stores/organizations-store";
import { useSteksManager } from "@/stores/steks-store";
import { useUserStore } from "@/stores/user-store";
import { ListboxChangeEvent } from "primevue/listbox";

const showSelectRole = ref(false);
const router = useRouter();
const route = useRoute();
useUserStore();

const { organization, showSelectOrganization, isLoadingOrganizations } =
    useOrganizationManager();
const { organizations } = useOrganizationsManager();
const { showSelectStek, isLoadingSteks } = useStekManager();
const { steks } = useSteksManager();

// Only show the spinner when loading items that are required to show
// the current page. For example, when loading a specific product,
// we might also be loading the list of processes for that product
// to be able to show the menu items for the selected product, however
// we don't need to be blocking the entire page with a loader for that.
//
// So, only show the spinner when we are not able to display anything
// that is of value to the user, or when we cannot show a spinner in
// the subsection that is being loaded.
const showSpinner = computed(() => {
    if (route.params.processName) {
        return [isLoadingOrganizations, isLoadingSteks].some(
            (ref) => ref.value === true
        );
    }

    if (route.params.productName) {
        return [isLoadingOrganizations, isLoadingSteks].some(
            (ref) => ref.value === true
        );
    }

    if (route.params.domainName) {
        return [isLoadingOrganizations, isLoadingSteks].some(
            (ref) => ref.value === true
        );
    }

    return false;
});

const selectOrganization = (event: ListboxChangeEvent) => {
    if (event.value) {
        router.push({
            name: "organizationPage",
            params: {
                orgName: event.value.name,
            },
        });
    }
};

const selectStek = (event: ListboxChangeEvent) => {
    if (event.value) {
        router
            .push({
                name: "stekPage",
                params: {
                    orgName: organization.value!.name,
                    stekName: event.value.name,
                },
            })
            .then(() => {
                showSelectStek.value = false;
            });
    }
};

const showOrganizationSelector = computed(
    () => showSelectOrganization.value && !showSpinner.value
);
const showRoleSelector = computed(
    () => showSelectRole.value && !showSpinner.value
);
const showStekSelector = computed(
    () =>
        showSelectStek.value &&
        !showSpinner.value &&
        !showSelectOrganization.value
);
</script>

<style></style>
